/**
 * 통화 형식으로 포맷팅하는 함수
 * @param value 숫자값
 * @param currency 통화 코드 (기본값: 'KRW')
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷팅된 통화 문자열
 */
export const formatCurrency = (value: number | string, currency: string = 'KRW', locale: string = 'ko-KR'): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  } catch (error) {
    // Intl.NumberFormat 실패 시 기본 포맷팅
    return `${numValue.toLocaleString(locale)}원`;
  }
};

/**
 * 숫자를 천단위 콤마로 포맷팅하는 함수
 * @param value 숫자값
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷팅된 숫자 문자열
 */
export const formatNumber = (value: number | string, locale: string = 'ko-KR'): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }
  
  return numValue.toLocaleString(locale);
};

/**
 * 날짜를 지정된 형식으로 포맷팅하는 함수
 * @param date 날짜 객체 또는 문자열
 * @param format 포맷 형식 (기본값: 'YYYY-MM-DD')
 * @param locale 로케일 (기본값: 'ko-KR')
 * @returns 포맷팅된 날짜 문자열
 */
export const formatDate = (date: Date | string, format: string = 'YYYY-MM-DD', locale: string = 'ko-KR'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return '';
  }
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateObj.getSeconds()).padStart(2, '0');
  
  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'YYYY-MM-DD HH:mm':
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    case 'YYYY-MM-DD HH:mm:ss':
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'locale':
      return dateObj.toLocaleDateString(locale);
    default:
      return dateObj.toLocaleDateString(locale);
  }
};

/**
 * 파일 크기를 사람이 읽기 쉬운 형식으로 포맷팅하는 함수
 * @param bytes 바이트 수
 * @param decimals 소수점 자리수 (기본값: 2)
 * @returns 포맷팅된 파일 크기 문자열
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * 전화번호를 포맷팅하는 함수
 * @param phoneNumber 전화번호 문자열
 * @param format 포맷 형식 (기본값: 'xxx-xxxx-xxxx')
 * @returns 포맷팅된 전화번호 문자열
 */
export const formatPhoneNumber = (phoneNumber: string, format: string = 'xxx-xxxx-xxxx'): string => {
  if (!phoneNumber) return '';
  
  // 숫자만 추출
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // 한국 전화번호 패턴에 맞게 포맷팅
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  } else if (cleaned.length === 9) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  
  return phoneNumber;
};

/**
 * 퍼센트를 포맷팅하는 함수
 * @param value 숫자값 (0-1 사이의 값 또는 0-100 사이의 값)
 * @param decimals 소수점 자리수 (기본값: 1)
 * @param isDecimal 값이 0-1 사이인지 여부 (기본값: false)
 * @returns 포맷팅된 퍼센트 문자열
 */
export const formatPercent = (value: number, decimals: number = 1, isDecimal: boolean = false): string => {
  if (isNaN(value)) return '0%';
  
  const percentValue = isDecimal ? value * 100 : value;
  return `${percentValue.toFixed(decimals)}%`;
};

/**
 * 사업자 등록번호를 포맷팅하는 함수
 * @param businessNumber 사업자 등록번호
 * @returns 포맷팅된 사업자 등록번호 문자열
 */
export const formatBusinessNumber = (businessNumber: string): string => {
  if (!businessNumber) return '';
  
  const cleaned = businessNumber.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
  }
  
  return businessNumber;
};

/**
 * 카드 번호를 포맷팅하는 함수
 * @param cardNumber 카드 번호
 * @param maskLength 마스킹할 길이 (기본값: 8)
 * @returns 포맷팅된 카드 번호 문자열
 */
export const formatCardNumber = (cardNumber: string, maskLength: number = 8): string => {
  if (!cardNumber) return '';
  
  const cleaned = cardNumber.replace(/\D/g, '');
  
  if (cleaned.length >= 16) {
    const first4 = cleaned.substring(0, 4);
    const last4 = cleaned.substring(cleaned.length - 4);
    const masked = '*'.repeat(maskLength);
    return `${first4}-${masked}-${last4}`;
  }
  
  return cardNumber;
};